import ApiController from './ApiController';
import DatabaseController from './DatabaseController';
import Permissao from '../models/Permissao';


/**
 * Mensagem de erro retornada pela API ou da validação local
 * @typedef Erro
 * @property {String} codigo Tipo do erro
 * @property {String} campo Campo em que o erro ocorreu
 * @property {String} mensagem Mensagem de erro em português
 */

/**
 * Lista de permissões retornadas pela API
 * @typedef Listagem
 * @property {Boolean} sucesso Indica se a requisição foi executada
 * @property {Array<Erro>} erros Listagem dos erros retornados
 * @property {Array<Permissao>} items Em caso de sucesso contém as permissões retornadas
 */

//Constantes com os perfis possíveis
const SUPER_ADMIN = 1;
const ADMIN = 2;
const EMPRESA = 3;
const AVALIADOR = 4;
const CLIENTE = 5;

//Enumerador com os perfils
const perfilAcesso = {
    SUPER_ADMIN,
    ADMIN,
    EMPRESA,
    AVALIADOR,
    CLIENTE,
    //Agrupamentos dos perfis
    ADMINISTRADORES: [SUPER_ADMIN, ADMIN],
    AVALIADORES: [EMPRESA, AVALIADOR],
    CLIENTES: [CLIENTE]
};

//Constantes com os módulos possíveis
const EMPRESAS = 'empresas';
const CLIENTES = 'clientes';
const AVALIADORES = 'avaliadores';
const ADMINISTRADORES = 'administradores';
const QUESTIONARIOS = 'questionarios';
const AVALIACOES = 'avaliacoes';
const RESULTADOS = 'resultados';

//Enumerador com os módulos
const modulos = {
    EMPRESAS,
    CLIENTES,
    AVALIADORES,
    ADMINISTRADORES,
    QUESTIONARIOS,
    AVALIACOES,
    RESULTADOS
};

//Lista com as permissões por perfil
const acessosPerfil = {
    [SUPER_ADMIN]: [EMPRESAS, CLIENTES, AVALIADORES, ADMINISTRADORES, QUESTIONARIOS, AVALIACOES, RESULTADOS],
    [ADMIN]: [EMPRESAS, CLIENTES, AVALIADORES, ADMINISTRADORES, QUESTIONARIOS, AVALIACOES, RESULTADOS],
    [EMPRESA]: [CLIENTES, AVALIADORES, QUESTIONARIOS, AVALIACOES, RESULTADOS],
    [AVALIADOR]: [CLIENTES, QUESTIONARIOS, AVALIACOES, RESULTADOS],
    [CLIENTE]: [RESULTADOS],
};

//Constantes com as permissões dos avaliadores
const PERMISSAO_CADASTRO_CLIENTES = 'CADASTRO_CLIENTE';
const PERMISSAO_CADASTRO_QUESTIONARIO = 'CADASTRO_QUESTIONARIO';
const PERMISSAO_VISUALIZAR_DASHBOARD_EMPRESA = 'DASHBOARD_EMPRESA';
const PERMISSAO_VISUALIZAR_TODAS_AVALIACOES = 'TODAS_AVALIACOES';
const PERMISSAO_ATRIBUICAO_CLIENTES = 'ATRIBUICAO_CLIENTES';

//Constantes com as permissões dos administradores
const PERMISSAO_CADASTRO_ADMINISTRADORES = 'CADASTRO_ADMINISTRADOR';
const PERMISSAO_VISUALIZAR_DASHBOARD_ADMIN = 'DASHBOARD_ADMIN';

/**
 * Controller para verificar as permissões do usuário
 */
class PermissaoController {

    constructor() {
        this.databaseController = new DatabaseController();
        this.apiController = new ApiController();
        this.usuario = null;
        this.empresa = null;
    }

    /**
     * Retorna os dados do usuário logado
     * @returns {Promise<Object>} Dados do usuário
     */
    async retornarUsuario() {
        if (this.usuario === null) {
            this.usuario = await this.databaseController.carregar('config', 'usuario');
        }

        return this.usuario;
    }

    /**
     * Retorna os dados da empresa relacionada ao usuário logado
     * @returns {Promise<Object>} Dados da empresa
     */
    async retornarEmpresa() {
        if (this.empresa === null) {
            this.empresa = await this.databaseController.carregar('config', 'empresa');
        }

        return this.empresa;
    }

    /**
     * Carrega o perfil do usuário do IndexedDB
     * @returns {Promise<Number>} Retorna o identificador do perfil
     */
    async retornarPerfil() {
        return (await this.retornarUsuario()).perfilAcesso;
    }

    /**
     * Carrega as permissões do usuário do IndexedDB
     * @returns {Promise<Array<Permissao>>} Retorna a lista de permissões concedidas para o usuário
     */
    async retornarPermissoes() {
        return (await this.retornarUsuario()).permissoes;
    }

    /**
     * Verifica se o usuário pode selecionar a empresa em uma lista
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteSelecionarEmpresa() {
        //Carrega o perfil
        const perfilUsuario = await this.retornarPerfil();
        //Verifica se está na lista de administradores
        return perfilAcesso.ADMINISTRADORES.includes(perfilUsuario);
    }

    /**
     * Verifica se o usuário pode selecionar o cliente em uma lista
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteSelecionarCliente() {
        //Carrega o perfil
        const perfilUsuario = await this.retornarPerfil();
        //Verifica se está na lista de administradores
        return !perfilAcesso.CLIENTES.includes(perfilUsuario);
    }

    /**
     * Verifica se o usuário pode realizar o cadastro de clientes
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteCadastrarCliente() {
        //Administradores possuem acesso garantido
        const perfil = await this.retornarPerfil();
        if (perfil === SUPER_ADMIN || perfil === ADMIN || perfil === EMPRESA)
            return true;
        //Avaliadores precisam da permissão
        else if (perfil === AVALIADOR) {
            //Carrega as permissões do usuário
            const permissoes = await this.retornarPermissoes();
            //Verifica se o usuário possui a permissão específica
            return !!permissoes.find(permissao => permissao.identificador === PERMISSAO_CADASTRO_CLIENTES);
            //Outros perfils não possuem permissão
        } else {
            return false;
        }
    }

    /**
     * Verifica se o usuário pode realizar o cadastro de questionários
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteCadastrarQuestionario() {
        //Administradores possuem acesso garantido
        const perfil = await this.retornarPerfil();
        if (perfil === SUPER_ADMIN || perfil === ADMIN || perfil === EMPRESA)
            return true;
        //Avaliadores precisam da permissão
        else if (perfil === AVALIADOR) {
            //Carrega as permissões do usuário
            const permissoes = await this.retornarPermissoes();
            //Verifica se o usuário possui a permissão específica
            return !!permissoes.find(permissao => permissao.identificador === PERMISSAO_CADASTRO_QUESTIONARIO);
            //Outros perfils não possuem permissão
        } else {
            return false;
        }
    }

    /**
     * Verifica se o usuário pode visualizar o dashboard da empresa
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteVisualizarDashboardEmpresa() {
        //Administradores possuem acesso garantido
        const perfil = await this.retornarPerfil();
        if (perfil === EMPRESA)
            return true;
        //Avaliadores precisam da permissão
        else if (perfil === AVALIADOR) {
            //Carrega as permissões do usuário
            const permissoes = await this.retornarPermissoes();
            //Verifica se o usuário possui a permissão específica
            return !!permissoes.find(permissao => permissao.identificador === PERMISSAO_VISUALIZAR_DASHBOARD_EMPRESA);
            //Outros perfils não possuem permissão
        } else {
            return false;
        }
    }

    /**
     * Verifica se o usuário pode visualizar avaliações feitas por outros usuários
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteVisualizarTodasAvaliacoes() {
        //Administradores possuem acesso garantido
        const perfil = await this.retornarPerfil();
        if (perfil === SUPER_ADMIN || perfil === ADMIN || perfil === EMPRESA)
            return true;
        //Avaliadores precisam da permissão
        else if (perfil === AVALIADOR) {
            //Carrega as permissões do usuário
            const permissoes = await this.retornarPermissoes();
            //Verifica se o usuário possui a permissão específica
            return !!permissoes.find(permissao => permissao.identificador === PERMISSAO_VISUALIZAR_TODAS_AVALIACOES);
            //Outros perfils não possuem permissão
        } else {
            return false;
        }
    }

    /**
     * Verifica se o usuário pode realizar atribuições de clientes
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteAtribuicaoClientes() {
        //Master admin possui o acesso garantido
        const perfil = await this.retornarPerfil();
        if (perfil === SUPER_ADMIN)
            return true;
        //Administradores e empresas possuem acesso
        else if (perfil === ADMIN || perfil === EMPRESA) {
            return true;
            //Outros perfils não possuem permissão
        } else {
            return false;
        }
    }

    /**
     * Verifica se o usuário pode realizar o cadastro de clientes
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteCadastrarAdministradores() {
        //Master admin possui o acesso garantido
        const perfil = await this.retornarPerfil();
        if (perfil === SUPER_ADMIN)
            return true;
        //Administradores precisam da permissão
        else if (perfil === ADMIN) {
            //Carrega as permissões do usuário
            const permissoes = await this.retornarPermissoes();
            //Verifica se o usuário possui a permissão específica
            return !!permissoes.find(permissao => permissao.identificador === PERMISSAO_CADASTRO_ADMINISTRADORES);
            //Outros perfils não possuem permissão
        } else {
            return false;
        }
    }

    /**
     * Verifica se o usuário pode realizar o cadastro de clientes
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário esteja autorizado
     */
    async permiteVisualizarDashboardAdmin() {
        //Master admin possui o acesso garantido
        const perfil = await this.retornarPerfil();
        if (perfil === SUPER_ADMIN)
            return true;
        //Administradores precisam da permissão
        else if (perfil === ADMIN) {
            //Carrega as permissões do usuário
            const permissoes = await this.retornarPermissoes();
            //Verifica se o usuário possui a permissão específica
            return !!permissoes.find(permissao => permissao.identificador === PERMISSAO_VISUALIZAR_DASHBOARD_ADMIN);
            //Outros perfils não possuem permissão
        } else {
            return false;
        }
    }

    /**
     * Verifica se o usuário pode acessar determinado módulo
     * @param {String} modulo Identificação do módulo
     * @returns {Promise<Boolean>} Retorna verdadeiro caso o usuário possua permissão para o módulo especificado
     */
    async possuiPermissao(modulo) {
        //Carrega o perfil do usuário
        const perfilUsuario = await this.retornarPerfil();
        //Verifica se o módulo está na lista de permissões
        return acessosPerfil[perfilUsuario].includes(modulo);
    }

    /**
     * Retorna a lista de módulos e a indicação se o usuário possui acesso a ele ou não
     * @returns {Promise<Object>} Retorna um objeto com a lista de módulos e a respecitiva indicação
     */
    async permissoesUsuario() {
        //Carrega o perfil so usuário
        const perfilUsuario = await this.retornarPerfil();
        //Retorna lista de módulos com a permissão
        return {
            [EMPRESAS]: acessosPerfil[perfilUsuario].includes(EMPRESAS),
            [CLIENTES]: acessosPerfil[perfilUsuario].includes(CLIENTES),
            [ADMINISTRADORES]: acessosPerfil[perfilUsuario].includes(ADMINISTRADORES) && await this.permiteCadastrarAdministradores(),
            [AVALIADORES]: acessosPerfil[perfilUsuario].includes(AVALIADORES),
            [QUESTIONARIOS]: acessosPerfil[perfilUsuario].includes(QUESTIONARIOS),
            [AVALIACOES]: acessosPerfil[perfilUsuario].includes(AVALIACOES),
            [RESULTADOS]: acessosPerfil[perfilUsuario].includes(RESULTADOS)
        };
    }

    /**
     * Retorna a lista de permissões habilitadas para os perfil de usuário
     * @param {Number} perfil Identificação do perfil a ser consultado
     * @returns {Promise<Listagem>} Retorna a listagem das permissões
     */
    async listarPermissoes(perfil) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: items } = await this.apiController.get('/permissao', { perfil });
            //Marca que houve retorno com sucesso
            const result = {
                sucesso: true,
                items: items.map(item => Permissao.clone(item))
            };
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            const erros = error.response ? error.response.data : [{ mensagem: error.message || 'Erro de conexão com o servidor' }];
            return { sucesso: false, erros };
        }
    }
}

export default PermissaoController;
export { perfilAcesso, modulos };